import { useEffect, useState } from 'react';
import Bitbucket from 'logos/Bitbucket';
import QodoAnteater from 'logos/QodoAnteater';
import { ArrowLeftRight } from 'lucide-react';
import { spawn } from 'utils/translations';
import { Loader } from '../../Loader/Loader';

const BITBUCKET_OAUTH_MESSAGE_TYPE = 'bitbucket-oauth-callback';

const OPENER_ORIGIN_KEY = 'bitbucket_oauth_opener_origin';

const EXCHANGE_PATH = '/git_integration/v1/bitbucket/oauth/exchange';

const API_BASE_URL_KEY = 'bitbucket_oauth_api_base_url';

const spawnCallback = spawn('dialogs.bitbucket-installation.oauth-callback');

class OAuthExchangeError extends Error {
  detail?: string;

  constructor(message: string, detail?: string) {
    super(message);
    this.detail = detail;
  }
}

const exchangeCodeForToken = async (code: string): Promise<string> => {
  const apiBaseUrl =
    sessionStorage.getItem(API_BASE_URL_KEY) || window.location.origin;
  const response = await fetch(`${apiBaseUrl}${EXCHANGE_PATH}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    let detail: string | undefined;

    try {
      const errorBody = await response.json();

      detail = errorBody?.detail;
    } catch {
      // ignore parse errors
    }

    throw new OAuthExchangeError(
      `Exchange failed with status ${response.status}`,
      detail,
    );
  }

  const data = await response.json();

  return data.token;
};

/**
 * Lightweight page rendered inside the OAuth popup.
 * Exchanges the authorization code for an encrypted token via the central backend,
 * then posts the token to the opener window and closes itself.
 * The opener origin is retrieved from sessionStorage (set by OAuthRedirectPage).
 */
export const OAuthCallbackReceiver = () => {
  const [status, setStatus] = useState<'loading' | 'error'>('loading');

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const openerOrigin = sessionStorage.getItem(OPENER_ORIGIN_KEY);

    if (error) {
      setStatus('error');

      return;
    }

    if (!window.opener || !openerOrigin) {
      console.warn('[OAuthCallback] Missing context', {
        hasOpener: !!window.opener,
        openerOrigin,
        apiBaseUrl: sessionStorage.getItem(API_BASE_URL_KEY),
      });

      return;
    }

    const postAndClose = (message: Record<string, unknown>) => {
      window.opener.postMessage(message, openerOrigin);
      sessionStorage.removeItem(OPENER_ORIGIN_KEY);
      sessionStorage.removeItem(API_BASE_URL_KEY);
      window.close();
    };

    if (error || !code) {
      postAndClose({
        type: BITBUCKET_OAUTH_MESSAGE_TYPE,
        error: error || 'no code',
      });

      return;
    }

    exchangeCodeForToken(code)
      .then((token) => {
        postAndClose({ type: BITBUCKET_OAUTH_MESSAGE_TYPE, token });
      })
      .catch((err) => {
        console.error('[OAuthCallback] Exchange failed', err);
        setStatus('error');
        // Post error to parent but don't close — keep popup open for debugging
        window.opener.postMessage(
          {
            type: BITBUCKET_OAUTH_MESSAGE_TYPE,
            error: `exchange failed: ${err.message}`,
            ...(err instanceof OAuthExchangeError &&
              err.detail && { errorDetail: err.detail }),
          },
          openerOrigin,
        );
      });
  }, []);

  const isError = status === 'error';

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-6">
      <div className="flex items-center gap-4">
        <QodoAnteater size={48} />
        <ArrowLeftRight className="text-text-bg-neutral-mid" size={20} />
        <Bitbucket size={48} />
      </div>

      <p className="text-h5 text-text-bg-neutral-strong">
        {spawnCallback('title')}
      </p>

      {isError ? (
        <p className="text-sm text-text-bg-neutral-mid">
          {spawnCallback('error')}
        </p>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Loader />
          <p className="text-sm text-text-bg-neutral-mid">
            {spawnCallback('loading')}
          </p>
        </div>
      )}
    </div>
  );
};

export { BITBUCKET_OAUTH_MESSAGE_TYPE, OPENER_ORIGIN_KEY, API_BASE_URL_KEY };
